import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, MoreHorizontal, Edit3, Trash2 } from 'lucide-react';
import { Column, Product, PriorityScoringConfig } from '../types';
import ProductCard from './ProductCard';
import { useDroppable } from '@dnd-kit/core';

interface KanbanColumnProps {
  column: Column;
  products: Product[];
  boardType?: 'product' | 'opportunity';
  onAddProduct: (columnId: string) => void;
  onProductClick: (product: Product) => void;
  onCommentClick: (product: Product, e: React.MouseEvent) => void;
  onProductMove?: (productId: string, newColumnId: string) => void;
  onEditColumn?: (column: Column) => void;
  onDeleteColumn?: (columnId: string) => void;
  scoringConfig?: PriorityScoringConfig;
}

const KanbanColumn: React.FC<KanbanColumnProps> = ({
  column,
  products,
  boardType = 'product',
  onAddProduct,
  onProductClick,
  onCommentClick,
  onProductMove,
  onEditColumn,
  onDeleteColumn,
  scoringConfig,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
  });


  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleEditColumn = () => {
    setIsMenuOpen(false);
    onEditColumn?.(column);
  };

  const handleDeleteColumn = () => {
    setIsMenuOpen(false);
    if (window.confirm(`Are you sure you want to delete the "${column.title}" column? This will move all products to the first available column.`)) {
      onDeleteColumn?.(column.id);
    }
  };

  return (
    <motion.div
      ref={setNodeRef}
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`w-full kanban-column ${isOver ? 'ring-2 ring-satsuma-300' : ''}`}
    >
      {/* Column Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <div 
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: column.color }}
          />
          <h3 className="font-semibold text-gray-800">{column.title}</h3>
          <span className="bg-white/15 text-gray-600 text-xs px-2 py-1 rounded-full">
            {products.length}
          </span>
        </div>
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded"
          >
            <MoreHorizontal size={16} />
          </button>
          
          {/* Dropdown Menu */}
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10"
            >
              <button
                onClick={handleEditColumn}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
              >
                <Edit3 size={14} />
                <span>Edit Column</span>
              </button>
              <button
                onClick={handleDeleteColumn}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
              >
                <Trash2 size={14} />
                <span>Delete Column</span>
              </button>
            </motion.div>
          )}
        </div>
      </div>

      {/* Products */}
      <div className="space-y-3 flex-1">
        {products.map((product) => (
          <ProductCard
            key={product.id}
            id={product.id}
            product={product}
            onClick={() => onProductClick(product)}
            onCommentClick={(e) => onCommentClick(product, e)}
            scoringConfig={scoringConfig}
          />
        ))}
      </div>

      {/* Add Product Button */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => onAddProduct(column.id)}
        className="w-full mt-4 p-3 border-2 border-dashed border-white/40 rounded-xl text-gray-500 hover:text-gray-700 hover:border-white/60 transition-all duration-200 flex items-center justify-center space-x-2 group"
      >
        <Plus size={16} className="group-hover:scale-110 transition-transform" />
        <span className="text-sm font-medium">
          Add {boardType === 'opportunity' ? 'Opportunity' : 'Product'}
        </span>
      </motion.button>
    </motion.div>
  );
};

export default KanbanColumn;
